import { ILeafFilterClause } from '../ServerApi/types/IFilterClause';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { IPage } from '../ServerApi/types/IPage';

export class ContentQueryBuilder{
  private baseUrl: string;
  private filterClauses: ILeafFilterClause[];
  private orderClauses: IOrderClause[];
  private page: IPage;

  constructor(url: string) {
    this.baseUrl = url;
    this.filterClauses = [];
    this.orderClauses = [];
  }

  withFilterClauses(filterClauses: ILeafFilterClause[]): ContentQueryBuilder {
    this.filterClauses = filterClauses;
    return this;
  }

  withSortClauses(orderClauses: IOrderClause[]): ContentQueryBuilder {
    this.orderClauses = orderClauses;
    return this;
  }

  withPaging(page: IPage): ContentQueryBuilder {
    this.page = page;
    return this;
  }


  build(): string{
    let searchParams = [];
    let sortExpression = this.buildSortExpression();
    let filterExpression = this.buildFilterExpression();
    let pagingExpression = this.buildPagingExpression();

    if(sortExpression){
      searchParams.push(sortExpression);
    }
    if(filterExpression){
      searchParams.push(filterExpression);
    }

    if(pagingExpression){
      searchParams.push(pagingExpression);
    }

    if(searchParams.length > 0){
      // TODO: remove this code once the type filter is being parsed
      if(this.baseUrl.indexOf('?') > -1){
        return `${this.baseUrl}&${searchParams.join('&')}`;
      }
      else{
        return `${this.baseUrl}?${searchParams.join('&')}`;
      }
    }
    else {
      return this.baseUrl;
    }
  }

  private static sortMap: Record<string, (a: string) => void> = {
    name: (direction: string)  => `title${direction}`,
    metricValue: (direction: string)  => `metric(value${direction})`,
    metricViewName: (direction: string)  => `metric(viewName${direction})`,
    ownerName: (direction: string)  => `metric(ownerUserName${direction})`,
    updatedAt: (direction: string)  => `metric(lastModified${direction})`,
    metricUpdated: (direction: string)  => `metric(lastUpdated${direction})`
  };

  private static filterMap: Record<string, (a: string) => void> = {
    singleContentType: (type: string)  => `type:${type}`,
    multipleContentTypes: (type: string)  => `${type}(type:${type})`,
    ownerUserName: (value: string)  => `metric(ownerUserName:"${value}")`,
    contentId: (value: string)  => `metric(metricId:"${value}")`
  };

  private buildSortExpression(){
    if(!this.orderClauses || this.orderClauses.length === 0) return '';

    let sortExpression = this.orderClauses.map((clause) => {
      const directionString = clause.ascending ? '' : ' desc';
      if(!ContentQueryBuilder.sortMap[clause.field]){
        throw new Error(`${clause.field} is not a valid sort expression`);
      }

      return ContentQueryBuilder.sortMap[clause.field](directionString);
    }).reduce((prev, curr) => (prev ? prev + ',' : '') + curr, '');

    return (sortExpression ? `sort=${sortExpression}` : '');
  }

  private buildFilterExpression(): string {
    const filterExpression = [];
    const contentTypes = [];
    for(let leafFilter of this.filterClauses){
      const filter = leafFilter as any;
      if(filter.operator === 'matches'){
        filterExpression.push(`global:${(filter as any).value}`);
      }
      else{ //TODO: we need to support other operators
        if (filter.field === 'contentType') {
          if (filter.value) {
            contentTypes.push(filter.value);
          } else if (filter.values) {
            for(let type of filter.values) {
              contentTypes.push(type);
            }
          }
        } else if(filter.value){
          filterExpression.push(ContentQueryBuilder.filterMap[filter.field](filter.value));
        } else if(filter.values){
          for(let value of filter.values){
            filterExpression.push(ContentQueryBuilder.filterMap[filter.field](value));
          }
        }
      }
    }

    if (contentTypes.length > 1) {
      for(let type of contentTypes) {
        filterExpression.push(ContentQueryBuilder.filterMap.multipleContentTypes(type));
      }
    } else if (contentTypes.length > 0) {
      filterExpression.push(ContentQueryBuilder.filterMap.singleContentType(contentTypes[0]));
    }

    const filterString = filterExpression.join(' ');
    return (filterString ? `q=${filterString}` : '');
  }

  private buildPagingExpression(): string {
    let pagingExpressions: Array<string> = [];
    if(!this.page) return '';
    if(this.page.startIndex != null){
      pagingExpressions.push(`page=${this.page.startIndex}`);
    }
    if(this.page.maxItems != null){
      pagingExpressions.push(`limit=${this.page.maxItems}`);
    }
    return pagingExpressions.join('&');
  }
}
